import { useCallback, useEffect, useState } from 'react'
import { ScrollText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input, Select } from '@/components/ui/input'
import { Badge, EmptyState, ErrorNote, Spinner } from '@/components/ui/misc'
import { api, ApiError, type AuditEntry } from '@/lib/api'
import { formatBytes, formatDateTime } from '@/lib/format'

const RESULT_TONES = {
  SUCCESS: 'success',
  BLOCKED: 'warning',
  FAILURE: 'danger',
} as const

interface Filters {
  username: string
  action: string
  result: string
  q: string
}

const EMPTY_FILTERS: Filters = { username: '', action: '', result: '', q: '' }

export function AuditPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [facets, setFacets] = useState<{ usernames: string[]; actions: string[] }>({
    usernames: [],
    actions: [],
  })

  useEffect(() => {
    api.auditFacets().then(setFacets).catch(() => {
      // Filter dropdowns fall back to free text if this fails.
    })
  }, [])

  const load = useCallback(
    async (continuation: string | null) => {
      if (continuation) setLoadingMore(true)
      else {
        setLoading(true)
        setError(null)
      }

      try {
        const res = await api.audit({
          cursor: continuation,
          username: filters.username || undefined,
          action: filters.action || undefined,
          result: filters.result || undefined,
          q: filters.q || undefined,
        })
        setEntries((current) => (continuation ? [...current, ...res.entries] : res.entries))
        setCursor(res.nextCursor)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load the audit log')
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [filters],
  )

  // Debounced so typing in the key filter does not fire a request per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => void load(null), 250)
    return () => window.clearTimeout(timer)
  }, [load])

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border px-5 py-4">
        <h1 className="text-lg font-semibold">Audit log</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every action taken through this app. HCP sees a single shared service account, so this is
          the only record of who did what.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 border-b border-border px-5 py-3">
        <div className="w-44">
          <Select
            value={filters.username}
            onChange={(e) => setFilters((f) => ({ ...f, username: e.target.value }))}
            aria-label="Filter by user"
          >
            <option value="">All users</option>
            {facets.usernames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        </div>

        <div className="w-44">
          <Select
            value={filters.action}
            onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
            aria-label="Filter by action"
          >
            <option value="">All actions</option>
            {facets.actions.map((action) => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </Select>
        </div>

        <div className="w-40">
          <Select
            value={filters.result}
            onChange={(e) => setFilters((f) => ({ ...f, result: e.target.value }))}
            aria-label="Filter by result"
          >
            <option value="">Any result</option>
            <option value="SUCCESS">Success</option>
            <option value="BLOCKED">Blocked</option>
            <option value="FAILURE">Failure</option>
          </Select>
        </div>

        <div className="min-w-48 flex-1">
          <Input
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Filter by object key…"
            aria-label="Filter by object key"
          />
        </div>

        <Button onClick={() => setFilters(EMPTY_FILTERS)}>Reset</Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error && (
          <div className="p-5">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <Spinner className="h-5 w-5" />
          </div>
        )}

        {!loading && entries.length === 0 && !error && (
          <EmptyState
            icon={<ScrollText className="h-8 w-8" />}
            title="No matching activity"
            description="Nothing has been recorded for these filters yet."
          />
        )}

        {entries.length > 0 && (
          <table className="w-full min-w-[52rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="w-44 px-5 py-2.5 font-medium">When</th>
                <th className="w-32 px-2 py-2.5 font-medium">User</th>
                <th className="w-32 px-2 py-2.5 font-medium">Action</th>
                <th className="px-2 py-2.5 font-medium">Object</th>
                <th className="w-24 px-2 py-2.5 text-right font-medium">Size</th>
                <th className="w-28 px-5 py-2.5 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-border align-top">
                  <td className="px-5 py-2.5 tabular text-muted-foreground">
                    {formatDateTime(entry.at)}
                  </td>
                  <td className="px-2 py-2.5 font-medium">{entry.username}</td>
                  <td className="px-2 py-2.5">{entry.action}</td>
                  <td className="px-2 py-2.5">
                    {entry.key ? (
                      <span className="font-mono text-xs">
                        {entry.bucket && (
                          <span className="text-muted-foreground">{entry.bucket}/</span>
                        )}
                        {entry.key}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                    {entry.detail && (
                      <span className="block text-xs text-muted-foreground">{entry.detail}</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-right tabular text-muted-foreground">
                    {entry.bytes === null ? '—' : formatBytes(entry.bytes)}
                  </td>
                  <td className="px-5 py-2.5">
                    <Badge
                      tone={RESULT_TONES[entry.result as keyof typeof RESULT_TONES] ?? 'neutral'}
                    >
                      {entry.result}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {cursor && (
          <div className="flex justify-center py-4">
            <Button onClick={() => void load(cursor)} loading={loadingMore}>
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
